import { SpecialActionIds, type SpecialActionTypeIds } from '@soulhx/fs-common';
import { useMemo } from 'react';
import { useAsyncLiveQuery } from './useAsyncLiveQuery.js';

const EMPTY_SPECIAL_ACTION_TYPES: SpecialActionTypeIds = {
  longNT: '',
  shortNT: '',
  longOT: '',
  prayer: '',
  shortOT: '',
  book: '',
  church: '',
  podcast: '',
  journal: '',
  scripture: '',
};

/**
 * Live-reactive resolution of the well-known "special" action types (see
 * `SpecialActionIds` in fs-common) against the locally-synced catalog,
 * instead of the separate `GET /do/t/specialTypes` round trip the old
 * `useGetSpecialTypeIDsQuery` hook made. Unlike that endpoint - which 500s
 * if a special type is somehow missing from the DB - a slot stays `''` here
 * if its type hasn't synced down yet, same "resolve whatever's known
 * locally" philosophy as `resolvePassageHashtagIds` in PassageSync.ts;
 * callers render whatever resolves rather than the whole feature failing.
 *
 * Built on `useAsyncLiveQuery` so a background sync writing/renaming a
 * special type re-resolves the affected slot(s) without the caller having
 * to re-fetch.
 *
 * Queries via the `specialActionId` index (rather than `.filter()`) so
 * Dexie's live-query observability only tracks the handful of rows that
 * actually have a special ID, instead of the whole `actionTypes` table -
 * otherwise every write to any action type (special or not) would
 * re-resolve this. The result is also kept at a stable object reference
 * across re-renders when the resolved IDs haven't changed, since callers
 * (e.g. `useGetActionEntriesForDay`/`ForMonth`) key their own live-query
 * re-subscription on this object's identity.
 */
export const useSpecialActionTypes = (): { data: SpecialActionTypeIds; isLoading: boolean } => {
  const { data, isLoading } = useAsyncLiveQuery(
    async (dbInstance) => {
      const specialTypes = await dbInstance.actionTypes
        .where('specialActionId')
        .anyOf(Object.values(SpecialActionIds))
        .toArray();
      const idBySpecialActionId = new Map(specialTypes.map((t) => [t.specialActionId, t.id]));

      const responseObj: SpecialActionTypeIds = { ...EMPTY_SPECIAL_ACTION_TYPES };
      for (const field of Object.keys(SpecialActionIds) as (keyof SpecialActionTypeIds)[]) {
        const match = idBySpecialActionId.get(SpecialActionIds[field]);
        if (match) responseObj[field] = match;
      }

      return responseObj;
    },
    [],
    EMPTY_SPECIAL_ACTION_TYPES
  );

  const stableData = useMemo(
    () => data,
    [
      data.shortOT,
      data.longOT,
      data.shortNT,
      data.longNT,
      data.prayer,
      data.book,
      data.church,
      data.podcast,
      data.journal,
      data.scripture,
    ]
  );

  return { data: stableData, isLoading };
};
